/**
 * 1.3.0, the media portal: external production companies deliver into placeholders, and chosen companies or users
 * upload footage without one (open uploads), through a portal on its own address. Every portal submission is its own
 * hidden *intake asset* (assets.intake_pending_at), reviewed in Intake before it reaches the Library.
 *
 * This file is the shared contract of the four build streams (docs/dev/portal-1.3.0-build.md): the tables' DTOs,
 * the API payloads, the state machines, the error codes, the email kinds and the English string catalogue. The API
 * parses request bodies with these schemas; the web app (Intake, Administration → Media portal, Assign for
 * delivery…) and the portal app (portal.html) type their responses with the DTOs.
 */
import { z } from 'zod';
import { COMMON_STRINGS } from './portal-strings/common.ts';
import { IDENTITY_STRINGS } from './portal-strings/identity.ts';
import { ADMIN_STRINGS } from './portal-strings/admin.ts';
import { ASSIGN_STRINGS } from './portal-strings/assign.ts';
import { PORTAL_APP_STRINGS } from './portal-strings/portal.ts';
import { INTAKE_STRINGS } from './portal-strings/intake.ts';
import { EXTRAS_Q1_STRINGS } from './portal-strings/extras-q1.ts';
import { EXTRAS_Q2_STRINGS } from './portal-strings/extras-q2.ts';
import { EXTRAS_Q3_STRINGS } from './portal-strings/extras-q3.ts';
import { EXTRAS_Q4_STRINGS } from './portal-strings/extras-q4.ts';
import { GROUPS_G2_STRINGS } from './portal-strings/groups-g2.ts';
import { GROUPS_G3_STRINGS } from './portal-strings/groups-g3.ts';

/* ================================================================ permissions, error codes */

/** The portal's own permissions (also in PERMISSIONS, roles.ts). */
export const PORTAL_PERMISSIONS = ['portal.manage', 'portal.review', 'portal.assign'] as const;
export type PortalPermission = (typeof PORTAL_PERMISSIONS)[number];

export const PORTAL_ERRORS = {
  /** 404 on the portal host while the workspace's portal is off (portal_settings.enabled false). */
  PORTAL_DISABLED: 'PORTAL_DISABLED',
  /** 404: a portal route asked for on PRISM's own host, or a member route asked for on the portal host. */
  PORTAL_HOST_ONLY: 'PORTAL_HOST_ONLY',
  /** 409: Accept while something still runs for the version (ingest, QC, transcription, agent jobs, transfers). */
  INTAKE_STILL_PROCESSING: 'INTAKE_STILL_PROCESSING',
  /** 409: Accept to Library (or Accept with missing items) before the media is accepted. */
  INTAKE_MEDIA_REQUIRED: 'INTAKE_MEDIA_REQUIRED',
  /** 409: a second assignment for a placeholder that has one not finished (open, in_progress, in_review). */
  ASSIGNMENT_OPEN: 'ASSIGNMENT_OPEN',
  /** 507 (1.3.1): an upload that would pass the company's monthly or pending quota. */
  PORTAL_QUOTA: 'PORTAL_QUOTA',
  /** 422: a submission without the rights declaration accepted (or with an older version of its text). */
  RIGHTS_REQUIRED: 'RIGHTS_REQUIRED',
  /** 422: ClamAV found something in the file; the upload is refused and its bytes deleted. */
  VIRUS_FOUND: 'VIRUS_FOUND',
  /** 409: Accept to Library while required items are not accepted yet (details.withMissing: Accept with missing items would work). */
  INTAKE_ITEMS_MISSING: 'INTAKE_ITEMS_MISSING',
  /** 409: Accept to Library before the submission can be accepted (nothing decided yet, or nothing uploaded). */
  INTAKE_NOT_READY: 'INTAKE_NOT_READY',
  /** 409: the submission's assignment was cancelled: it can only be rejected. */
  ASSIGNMENT_CANCELLED: 'ASSIGNMENT_CANCELLED',
  /** 409: removing a QC profile, collection, asset class or storage location a delivery spec, upload rule or the portal's settings use. */
  PORTAL_REFERENCED: 'PORTAL_REFERENCED',
  /** 409 (1.3.1, Q2): an upload (or submit) for an assignment that expired (portal_settings.expiry_days after its due date). */
  ASSIGNMENT_EXPIRED: 'ASSIGNMENT_EXPIRED',
  /** 422 (1.3.1, Q1): an email text override with a {placeholder} its kind does not have (details.unknown: the names). */
  EMAIL_TEXT_PLACEHOLDER: 'EMAIL_TEXT_PLACEHOLDER',
  /** 422 (1.3.1, Q1): a logo that is not a PNG or an SVG, is too large, or whose SVG could not be made safe. */
  LOGO_INVALID: 'LOGO_INVALID',
} as const;
export type PortalErrorCode = (typeof PORTAL_ERRORS)[keyof typeof PORTAL_ERRORS];

/* ================================================================ state machines */

/** delivery_assignments.state: open → in_progress → in_review → accepted | cancelled | expired. */
export const ASSIGNMENT_STATES = ['open', 'in_progress', 'in_review', 'accepted', 'cancelled', 'expired'] as const;
export type AssignmentState = (typeof ASSIGNMENT_STATES)[number];
/** Not finished: one per placeholder (delivery_assignments_one_open). */
export const ASSIGNMENT_ACTIVE_STATES: readonly AssignmentState[] = ['open', 'in_progress', 'in_review'];
export const ASSIGNMENT_TRANSITIONS: Record<AssignmentState, readonly AssignmentState[]> = {
  open: ['in_progress', 'in_review', 'cancelled', 'expired'],
  in_progress: ['in_review', 'open', 'cancelled', 'expired'],
  // Back to in_progress when an item is rejected and the supplier uploads again.
  in_review: ['in_progress', 'accepted', 'cancelled', 'expired'],
  // Accepted with missing items stays accepted while the missing items arrive (they land on the Library asset).
  accepted: [],
  cancelled: [],
  // Reopened with a new due date (1.3.1): open again, or in progress when it still has files.
  expired: ['open', 'in_progress'],
};
/** delivery_assignments.outcome once accepted. */
export const ASSIGNMENT_OUTCOMES = ['complete', 'missing_items'] as const;
export type AssignmentOutcome = (typeof ASSIGNMENT_OUTCOMES)[number];

/** delivery_items.state. */
export const ITEM_STATES = ['open', 'uploaded', 'accepted', 'rejected', 'waived'] as const;
export type ItemState = (typeof ITEM_STATES)[number];
export const ITEM_TRANSITIONS: Record<ItemState, readonly ItemState[]> = {
  open: ['uploaded', 'waived'],
  uploaded: ['accepted', 'rejected', 'uploaded', 'open'],
  // A rejected item is uploaded again by the supplier.
  rejected: ['uploaded', 'waived'],
  accepted: [],
  waived: ['open'],
};

/** intake_submissions.state: draft → waiting ⇄ needs_changes → accepted | rejected | withdrawn | expired. */
export const SUBMISSION_STATES = ['draft', 'waiting', 'needs_changes', 'accepted', 'rejected', 'withdrawn', 'expired'] as const;
export type SubmissionState = (typeof SUBMISSION_STATES)[number];
export const SUBMISSION_TRANSITIONS: Record<SubmissionState, readonly SubmissionState[]> = {
  draft: ['waiting', 'withdrawn', 'expired'],
  waiting: ['needs_changes', 'accepted', 'rejected', 'withdrawn', 'expired'],
  needs_changes: ['waiting', 'rejected', 'withdrawn', 'expired'],
  accepted: [],
  rejected: [],
  withdrawn: [],
  expired: [],
};
/** Finished: purged with their files after the retention period, unless accepted. */
export const SUBMISSION_FINAL_STATES: readonly SubmissionState[] = ['accepted', 'rejected', 'withdrawn', 'expired'];
export const SUBMISSION_KINDS = ['delivery', 'open'] as const;
export type SubmissionKind = (typeof SUBMISSION_KINDS)[number];

/** Whether a state machine allows `from → to`. */
export function canTransition<S extends string>(machine: Record<S, readonly S[]>, from: S, to: S): boolean {
  return (machine[from] ?? []).includes(to);
}

/* ================================================================ requested items */

export const ITEM_KINDS = ['media', 'document', 'subtitles', 'image', 'audio', 'other'] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

/** Accepted-type presets for an extra item (extensions, lower case, no dot); `any` is empty: every type. */
export const ITEM_TYPE_PRESETS = {
  document: ['pdf', 'doc', 'docx', 'odt', 'rtf', 'txt', 'pages', 'xls', 'xlsx', 'ods', 'csv'],
  image: ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'psd', 'ai', 'eps', 'svg', 'webp'],
  subtitles: ['srt', 'vtt', 'stl', 'ttml', 'dfxp', 'scc'],
  audio: ['wav', 'mp3', 'aac', 'm4a', 'flac', 'aif', 'aiff'],
  any: [],
} as const satisfies Record<string, readonly string[]>;
export type ItemTypePreset = keyof typeof ITEM_TYPE_PRESETS;
/** The preset an item kind starts with. */
export const ITEM_KIND_PRESET: Record<Exclude<ItemKind, 'media'>, ItemTypePreset> = {
  document: 'document', subtitles: 'subtitles', image: 'image', audio: 'audio', other: 'any',
};
/** The asset_attachments.kind an accepted item becomes (subtitles in SRT or WebVTT become a subtitle track instead). */
export const ITEM_ATTACHMENT_KIND: Record<Exclude<ItemKind, 'media'>, 'document' | 'subtitle' | 'image' | 'audio' | 'other'> = {
  document: 'document', subtitles: 'subtitle', image: 'image', audio: 'audio', other: 'other',
};

const Extension = z.string().regex(/^[a-z0-9]{1,10}$/);

/** An extra item a spec starts every assignment with, or one added to an assignment (delivery_specs.default_items). */
export const DeliveryItemTemplate = z.object({
  kind: z.enum(ITEM_KINDS).exclude(['media']),
  label: z.string().trim().min(1).max(200),
  description: z.string().max(2000).default(''),
  acceptedTypes: z.array(Extension).max(50).default([]),
  required: z.boolean().default(true),
});
export type DeliveryItemTemplate = z.infer<typeof DeliveryItemTemplate>;

/* ================================================================ shared pieces */

/**
 * How a spec's or rule's submissions are accepted (0092 added the last two). Automatic modes act when QC finishes on
 * the media (they need a QC profile; without one every submission is reviewed):
 *   review              always reviewed in Intake;
 *   auto_qc_pass        accepted automatically when QC passes with no warnings; warnings and failures are reviewed;
 *   auto_qc_warnings    accepted automatically when QC passes, warnings allowed; failures are reviewed;
 *   review_on_warnings  accepted automatically when QC passes, reviewed when QC has warnings, and sent back to the
 *                       supplier (the media rejected with the QC result) when QC fails.
 * Automatic acceptance accepts the media; a delivery reaches the Library once its required items are accepted too.
 */
export const ACCEPTANCE_MODES = ['review', 'auto_qc_pass', 'auto_qc_warnings', 'review_on_warnings'] as const;
export type AcceptanceMode = (typeof ACCEPTANCE_MODES)[number];
/** What an acceptance mode does with a finished QC run's status: accept, leave for review, or send back. */
export function acceptanceAction(mode: AcceptanceMode, qc: 'passed' | 'warnings' | 'failed'): 'accept' | 'review' | 'send_back' {
  if (mode === 'review') return 'review';
  if (qc === 'passed') return 'accept';
  if (qc === 'warnings') return mode === 'auto_qc_warnings' ? 'accept' : 'review';
  return mode === 'review_on_warnings' ? 'send_back' : 'review';
}

/**
 * The Patch body of a Create schema: every field optional and **without its default** (zod 4's `.partial()` keeps
 * the defaults, so an omitted field would come back filled in and overwrite the stored value).
 */
type PatchShape<S extends z.ZodRawShape> = { [K in keyof S]: S[K] extends z.ZodDefault<infer I> ? z.ZodOptional<I> : z.ZodOptional<S[K]> };
export function patchOf<S extends z.ZodRawShape>(o: z.ZodObject<S>): z.ZodObject<PatchShape<S>> {
  const shape: Record<string, z.ZodType> = {};
  for (const [k, v] of Object.entries(o.shape)) shape[k] = (v instanceof z.ZodDefault ? (v.removeDefault() as z.ZodType) : (v as z.ZodType)).optional();
  return z.object(shape) as unknown as z.ZodObject<PatchShape<S>>;
}
export const TWO_STEP_POLICIES = ['required', 'optional'] as const;
export type TwoStepPolicy = (typeof TWO_STEP_POLICIES)[number];
export const COMPANY_TWO_STEP_POLICIES = ['inherit', 'required', 'optional'] as const;
export type CompanyTwoStepPolicy = (typeof COMPANY_TWO_STEP_POLICIES)[number];

/** Who reviews a spec's or rule's submissions: the task assignee model (a person, an access group or a role), or nobody in particular. */
export const ReviewerRef = z.discriminatedUnion('type', [
  z.object({ type: z.literal('user'), id: z.string().uuid() }),
  z.object({ type: z.literal('group'), id: z.string().uuid() }),
  z.object({ type: z.literal('role'), key: z.string().regex(/^[a-z0-9_]{1,40}$/) }),
]);
export type ReviewerRef = z.infer<typeof ReviewerRef>;
/** A reviewer as the API answers it. */
export type ReviewerDTO = { type: 'user'; id: string; name: string } | { type: 'group'; id: string; name: string } | { type: 'role'; key: string; name: string } | null;

/** 500 GB, PRISM_PORTAL_UPLOAD_MAX_BYTES' default and portal_settings.max_upload_bytes'. */
export const PORTAL_UPLOAD_MAX_BYTES_DEFAULT = 500 * 1024 ** 3;

/* ================================================================ portal_settings (P1) */

export interface PortalSettingsDTO {
  enabled: boolean;
  /** The portal's origin; null: PRISM_PORTAL_ORIGIN. `effectiveOrigin` is what is used. */
  origin: string | null;
  effectiveOrigin: string | null;
  twoStepPolicy: TwoStepPolicy;
  defaultAcceptance: AcceptanceMode;
  maxUploadBytes: number;
  intakeLocationId: string | null;
  retentionDays: number;
  /** Minutes a signed-in supplier may be idle before the portal signs them out (0: never; 0092). */
  idleMinutes: number;
  reminderOffsetsDays: number[];
  expiryDays: number | null;
  rightsText: string;
  rightsVersion: number;
  branding: PortalBrandingDTO;
  /**
   * ClamAV: configured (PRISM_PORTAL_CLAMAV_URL), and its last answer. 1.3.1: also PING's round trip, the engine, the
   * signature database's version and date (from VERSION) and the error (System status → Services shows the same).
   */
  clamav: {
    configured: boolean; reachable: boolean | null; version: string | null; checkedAt: string | null;
    latencyMs?: number | null; engine?: string | null; signatures?: string | null; signaturesAt?: string | null; error?: string | null;
  };
  updatedAt: string | null;
}
export interface PortalBrandingDTO {
  portalName: string | null;
  welcomeText: string;
  helpText: string;
  accentColor: string | null;
  /** 1.3.1: the logo's URL on the portal host, or null. */
  logoUrl: string | null;
}
export const PortalSettingsPatch = z.object({
  enabled: z.boolean(),
  origin: z.string().url().max(300).nullable(),
  twoStepPolicy: z.enum(TWO_STEP_POLICIES),
  defaultAcceptance: z.enum(ACCEPTANCE_MODES),
  maxUploadBytes: z.number().int().positive(),
  intakeLocationId: z.string().uuid().nullable(),
  retentionDays: z.number().int().min(1).max(3650),
  idleMinutes: z.number().int().min(0).max(1440),
  reminderOffsetsDays: z.array(z.number().int().min(1).max(60)).max(5),
  expiryDays: z.number().int().min(1).max(365).nullable(),
  /** Changing the text raises rightsVersion. */
  rightsText: z.string().max(20000),
  portalName: z.string().max(120).nullable(),
  welcomeText: z.string().max(5000),
  helpText: z.string().max(5000),
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable(),
}).partial();
export type PortalSettingsPatch = z.infer<typeof PortalSettingsPatch>;

/* ================================================================ companies and users (P1) */

export interface PortalCompanyDTO {
  id: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  notes: string;
  twoStepPolicy: CompanyTwoStepPolicy;
  /** 1.3.1. */
  quotaBytesMonth: number | null;
  quotaBytesPending: number | null;
  disabled: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}
export const PortalCompanyCreate = z.object({
  name: z.string().trim().min(1).max(200),
  contactName: z.string().max(200).nullable().default(null),
  contactEmail: z.string().email().max(320).nullable().default(null),
  notes: z.string().max(5000).default(''),
  twoStepPolicy: z.enum(COMPANY_TWO_STEP_POLICIES).default('inherit'),
  /** 1.3.1: the bytes the company may upload per calendar month (UTC), and may have waiting at once; null: no limit. */
  quotaBytesMonth: z.number().int().positive().nullable().default(null),
  quotaBytesPending: z.number().int().positive().nullable().default(null),
});
export type PortalCompanyCreate = z.infer<typeof PortalCompanyCreate>;
export const PortalCompanyPatch = patchOf(PortalCompanyCreate).extend({
  disabled: z.boolean().optional(),
  quotaBytesMonth: z.number().int().positive().nullable().optional(),
  quotaBytesPending: z.number().int().positive().nullable().optional(),
});
export type PortalCompanyPatch = z.infer<typeof PortalCompanyPatch>;

export type PortalUserStatus = 'invited' | 'active' | 'disabled';
export interface PortalUserDTO {
  id: string;
  companyId: string;
  companyName: string;
  email: string;
  displayName: string;
  status: PortalUserStatus;
  twoStepEnabled: boolean;
  lastSignInAt: string | null;
  /** An invitation not accepted yet: when it expires. */
  invitationExpiresAt: string | null;
  createdAt: string;
}
export const PortalUserInvite = z.object({
  email: z.string().trim().email().max(320),
  displayName: z.string().trim().min(1).max(200),
});
export type PortalUserInvite = z.infer<typeof PortalUserInvite>;
export const PortalUserPatch = z.object({
  displayName: z.string().trim().min(1).max(200),
  disabled: z.boolean(),
  companyId: z.string().uuid(),
}).partial();
export type PortalUserPatch = z.infer<typeof PortalUserPatch>;

/* ================================================================ delivery specs and upload rules (P1) */

export interface DeliverySpecDTO {
  id: string;
  name: string;
  description: string;
  qcProfileId: string | null;
  acceptedTypes: string[];
  maxBytes: number | null;
  /** Metadata fields the supplier fills in (0092), and which of them are required (requiredFields ⊆ supplierFields). */
  supplierFields: string[];
  requiredFields: string[];
  namingRule: string | null;
  /** null: the portal's default acceptance. */
  acceptance: AcceptanceMode | null;
  reviewer: ReviewerDTO;
  defaultItems: DeliveryItemTemplate[];
  disabled: boolean;
  /** Assignments not finished that use it. */
  activeAssignments: number;
  createdAt: string;
  updatedAt: string;
}
export const DeliverySpecCreate = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(5000).default(''),
  qcProfileId: z.string().uuid().nullable().default(null),
  acceptedTypes: z.array(Extension).max(50).default([]),
  maxBytes: z.number().int().positive().nullable().default(null),
  /** Optional and required fields together; a required field not listed here is added to it. */
  supplierFields: z.array(z.string().min(1).max(100)).max(100).default([]),
  requiredFields: z.array(z.string().min(1).max(100)).max(100).default([]),
  namingRule: z.string().max(300).nullable().default(null),
  acceptance: z.enum(ACCEPTANCE_MODES).nullable().default(null),
  reviewer: ReviewerRef.nullable().default(null),
  defaultItems: z.array(DeliveryItemTemplate).max(30).default([]),
});
export type DeliverySpecCreate = z.infer<typeof DeliverySpecCreate>;
export const DeliverySpecPatch = patchOf(DeliverySpecCreate).extend({ disabled: z.boolean().optional() });
export type DeliverySpecPatch = z.infer<typeof DeliverySpecPatch>;

export interface UploadRuleDTO {
  id: string;
  /** Exactly one of company or user. */
  companyId: string | null;
  portalUserId: string | null;
  subjectName: string;
  enabled: boolean;
  acceptedTypes: string[];
  maxBytes: number | null;
  qcProfileId: string | null;
  collectionId: string | null;
  categoryId: string | null;
  defaultMetadata: Record<string, unknown>;
  supplierFields: string[];
  requiredFields: string[];
  acceptance: AcceptanceMode;
  groupFiles: boolean;
  reviewer: ReviewerDTO;
  createdAt: string;
  updatedAt: string;
}
const UploadRuleFields = z.object({
  enabled: z.boolean().default(true),
  acceptedTypes: z.array(Extension).max(50).default([]),
  maxBytes: z.number().int().positive().nullable().default(null),
  qcProfileId: z.string().uuid().nullable().default(null),
  collectionId: z.string().uuid().nullable().default(null),
  categoryId: z.string().uuid().nullable().default(null),
  defaultMetadata: z.record(z.string(), z.unknown()).default({}),
  supplierFields: z.array(z.string().min(1).max(100)).max(50).default([]),
  requiredFields: z.array(z.string().min(1).max(100)).max(50).default([]),
  acceptance: z.enum(ACCEPTANCE_MODES).default('review'),
  groupFiles: z.boolean().default(false),
  reviewer: ReviewerRef.nullable().default(null),
});
export const UploadRuleCreate = UploadRuleFields.extend({
  companyId: z.string().uuid().nullable().default(null),
  portalUserId: z.string().uuid().nullable().default(null),
}).refine((r) => (r.companyId === null) !== (r.portalUserId === null), { message: 'A rule is for one company or one user' });
export type UploadRuleCreate = z.infer<typeof UploadRuleCreate>;
export const UploadRulePatch = patchOf(UploadRuleFields);
export type UploadRulePatch = z.infer<typeof UploadRulePatch>;

/* ================================================================ assignments and items (P2) */

export interface DeliveryItemDTO {
  id: string;
  assignmentId: string;
  position: number;
  kind: ItemKind;
  label: string;
  description: string;
  acceptedTypes: string[];
  required: boolean;
  state: ItemState;
  /** The current file, when one is uploaded: an attachment or (media) a version of the intake asset. */
  file: { filename: string; sizeBytes: number; attachmentId: string | null; versionId: string | null; uploadedAt: string } | null;
  submissionId: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  reason: string | null;
}
export interface DeliveryAssignmentDTO {
  id: string;
  /** The placeholder. */
  asset: { id: string; assetCode: string; title: string };
  company: { id: string; name: string };
  portalUser: { id: string; name: string } | null;
  spec: { id: string; name: string } | null;
  state: AssignmentState;
  outcome: AssignmentOutcome | null;
  dueAt: string | null;
  /** The time zone the due date was set in (IANA name; 0092), shown with it in emails. */
  dueTimeZone: string | null;
  overdue: boolean;
  notes: string;
  items: DeliveryItemDTO[];
  /** Who made the assignment. */
  assignedBy: { id: string; name: string } | null;
  /** The internal contact suppliers are told about (delivery_assignments.contact_user_id, 0092). */
  contact: { id: string; name: string; email: string } | null;
  createdAt: string;
  updatedAt: string;
}
export const DeliveryAssignmentCreate = z.object({
  /** The placeholders (one assignment each; a Library multi-selection sends several). */
  assetIds: z.array(z.string().uuid()).min(1).max(200),
  companyId: z.string().uuid(),
  portalUserId: z.string().uuid().nullable().default(null),
  specId: z.string().uuid().nullable().default(null),
  dueAt: z.string().datetime({ offset: true }).nullable().default(null),
  notes: z.string().max(5000).default(''),
  /** The extra items (the media item is always added); omitted: the spec's default items. */
  items: z.array(DeliveryItemTemplate).max(30).optional(),
});
export type DeliveryAssignmentCreate = z.infer<typeof DeliveryAssignmentCreate>;
export const DeliveryAssignmentPatch = z.object({
  portalUserId: z.string().uuid().nullable(),
  specId: z.string().uuid().nullable(),
  dueAt: z.string().datetime({ offset: true }).nullable(),
  notes: z.string().max(5000),
  /** Replaces the extra items not uploaded yet. */
  items: z.array(DeliveryItemTemplate.extend({ id: z.string().uuid().optional() })).max(30),
}).partial();
export type DeliveryAssignmentPatch = z.infer<typeof DeliveryAssignmentPatch>;
export interface DeliveryAssignmentCreateResult {
  created: DeliveryAssignmentDTO[];
  /** Placeholders refused: not a placeholder, not visible, or an assignment already open (ASSIGNMENT_OPEN). */
  refused: Array<{ assetId: string; code: string; message: string }>;
}

/* ================================================================ intake (P4) */

export interface RightsAcceptanceDTO {
  acceptedAt: string;
  acceptedBy: { id: string; name: string } | null;
  version: number;
  textSha256: string;
}
export interface IntakeDestination {
  collectionId?: string | null;
  categoryId?: string | null;
  metadata?: Record<string, unknown>;
  /** Link to a placeholder: the upload's media goes into this placeholder instead of becoming an asset. */
  placeholderAssetId?: string | null;
}
export interface IntakeSubmissionDTO {
  id: string;
  kind: SubmissionKind;
  state: SubmissionState;
  intakeAssetId: string | null;
  company: { id: string; name: string };
  portalUser: { id: string; name: string } | null;
  /** A delivery's assignment and its placeholder. */
  assignment: { id: string; asset: { id: string; assetCode: string; title: string }; dueAt: string | null } | null;
  uploadRule: { id: string; name: string } | null;
  items: DeliveryItemDTO[];
  /** The media: the intake asset's current version. */
  media: { versionId: string; filename: string; sizeBytes: number | null; status: string; qcStatus: string | null; thumbnailUrl: string | null } | null;
  reviewer: ReviewerDTO;
  supplierMetadata: Record<string, unknown>;
  destination: IntakeDestination;
  rights: RightsAcceptanceDTO | null;
  /** "Same file as ASSET_2026_123": a Library asset with this checksum (never refused). */
  duplicateOf: { id: string; assetCode: string; title: string } | null;
  resultAssetId: string | null;
  overdue: boolean;
  submittedAt: string | null;
  decidedAt: string | null;
  decidedBy: { id: string; name: string } | null;
  decisionReason: string | null;
  createdAt: string;
  updatedAt: string;
}
export const INTAKE_CHIPS = ['waiting', 'needs_changes', 'accepted', 'withdrawn', 'all'] as const;
export type IntakeChip = (typeof INTAKE_CHIPS)[number];
export const IntakeListQuery = z.object({
  chip: z.enum(INTAKE_CHIPS).default('waiting'),
  companyId: z.string().uuid().optional(),
  kind: z.enum(SUBMISSION_KINDS).optional(),
  /** 1.4.0 (G3): only deliveries of placeholders in this group or its sub-groups. */
  groupId: z.string().uuid().optional(),
  overdue: z.coerce.boolean().optional(),
  q: z.string().max(200).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type IntakeListQuery = z.infer<typeof IntakeListQuery>;
export interface IntakeListDTO { items: IntakeSubmissionDTO[]; nextCursor: string | null; counts: Record<IntakeChip, number> }
/** GET /api/v1/intake/summary: the rail badge. */
export interface IntakeSummaryDTO { waiting: number; overdue: number }

export const ItemDecisionBody = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('accept') }),
  z.object({ decision: z.literal('reject'), reason: z.string().trim().min(1).max(5000) }),
  z.object({ decision: z.literal('waive'), reason: z.string().max(5000).default('') }),
  z.object({ decision: z.literal('reopen') }),
]);
export type ItemDecisionBody = z.infer<typeof ItemDecisionBody>;
export const IntakeDestinationBody = z.object({
  collectionId: z.string().uuid().nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  placeholderAssetId: z.string().uuid().nullable().optional(),
});
export const IntakeAcceptBody = z.object({
  /** Accept with missing items: the media must be accepted; the missing items stay open on the assignment. */
  allowMissing: z.boolean().default(false),
  /** Open uploads: the destination (pre-filled from the rule), or a placeholder to link to. */
  destination: IntakeDestinationBody.optional(),
});
export type IntakeAcceptBody = z.infer<typeof IntakeAcceptBody>;
export const IntakeBulkAcceptBody = z.object({
  submissionIds: z.array(z.string().uuid()).min(1).max(200),
  destination: IntakeDestinationBody.optional(),
});
export type IntakeBulkAcceptBody = z.infer<typeof IntakeBulkAcceptBody>;
export const IntakeRejectBody = z.object({ reason: z.string().trim().min(1).max(5000) });
export type IntakeRejectBody = z.infer<typeof IntakeRejectBody>;
export const IntakeAssignBody = z.object({ reviewer: ReviewerRef.nullable() });
export type IntakeAssignBody = z.infer<typeof IntakeAssignBody>;
export interface IntakeAcceptResult { submissionId: string; assetId: string; assetCode: string; outcome: AssignmentOutcome | 'asset' }

/** intake_notes: a comment or a decision on a submission or one of its items. */
export const NOTE_DECISIONS = ['accepted', 'rejected', 'waived', 'reopened', 'withdrawn'] as const;
export type NoteDecision = (typeof NOTE_DECISIONS)[number];
export interface IntakeNoteDTO {
  id: string;
  submissionId: string | null;
  itemId: string | null;
  author: { kind: 'member' | 'portal'; id: string | null; name: string };
  body: string;
  /** Staff only: never returned by the portal API. */
  internal: boolean;
  decision: NoteDecision | null;
  createdAt: string;
  editedAt: string | null;
}
export const IntakeNoteCreate = z.object({
  body: z.string().trim().min(1).max(10000),
  itemId: z.string().uuid().nullable().default(null),
  internal: z.boolean().default(false),
});
export type IntakeNoteCreate = z.infer<typeof IntakeNoteCreate>;
/** The supplier's comment (no internal switch). */
export const PortalNoteCreate = IntakeNoteCreate.omit({ internal: true });
export type PortalNoteCreate = z.infer<typeof PortalNoteCreate>;

/* ================================================================ the portal API (P1 sign-in, P3 the rest) */

export const PortalSignInBody = z.object({ email: z.string().trim().email().max(320), password: z.string().min(1).max(1000) });
export type PortalSignInBody = z.infer<typeof PortalSignInBody>;
export const PortalCodeBody = z.object({ code: z.string().trim().min(6).max(40) });
export type PortalCodeBody = z.infer<typeof PortalCodeBody>;
export const PortalAcceptInvitationBody = z.object({ token: z.string().min(20).max(200), password: z.string().min(12).max(1000) });
export type PortalAcceptInvitationBody = z.infer<typeof PortalAcceptInvitationBody>;
export const PortalForgotBody = z.object({ email: z.string().trim().email().max(320) });
export const PortalResetBody = z.object({ token: z.string().min(20).max(200), password: z.string().min(12).max(1000) });
/** What a sign-in step answers: signed in, or the next step. */
export interface PortalSignInResult { next: 'done' | 'code' | 'enrol'; me?: PortalMeDTO }
export interface PortalTotpEnrolDTO { secret: string; otpauthUrl: string; qrSvg: string }
export interface PortalTotpConfirmDTO { recoveryCodes: string[] }

export interface PortalMeDTO {
  id: string;
  email: string;
  displayName: string;
  company: { id: string; name: string };
  twoStepEnabled: boolean;
  /** An upload rule applies: New upload is shown. */
  canUpload: boolean;
  workspaceName: string;
  branding: PortalBrandingDTO;
  /** The rights declaration to accept with each submission. */
  rights: { text: string; version: number };
}
/** GET /api/v1/portal/public: what the sign-in page shows before anyone signs in. */
export interface PortalPublicDTO { enabled: boolean; workspaceName: string; branding: PortalBrandingDTO }

/** A supplier's view of one assignment ("My deliveries"). No internal notes, no reviewer, no staff names beyond decisions. */
export interface PortalDeliveryDTO {
  id: string;
  asset: { assetCode: string; title: string };
  spec: { name: string; description: string; acceptedTypes: string[]; maxBytes: number | null; requiredFields: string[] } | null;
  state: AssignmentState;
  dueAt: string | null;
  notes: string;
  items: DeliveryItemDTO[];
  submission: { id: string; state: SubmissionState; qcStatus: string | null } | null;
  canSubmit: boolean;
  /** 1.4.0 (G3): the group assignment it came from (delivery_assignments.group_id), for My deliveries' grouping. */
  group?: import('./groups.ts').PortalDeliveryGroup | null;
}
export interface PortalUploadRuleDTO {
  acceptedTypes: string[];
  maxBytes: number | null;
  supplierFields: Array<{ id: string; label: string; type: string; required: boolean }>;
  groupFiles: boolean;
}
/** A supplier's view of one submission. */
export interface PortalSubmissionDTO {
  id: string;
  kind: SubmissionKind;
  state: SubmissionState;
  title: string;
  items: DeliveryItemDTO[];
  media: { filename: string; status: string; qcStatus: string | null; previewUrl: string | null } | null;
  qcFindings: Array<{ severity: string; message: string; atSeconds: number | null }>;
  supplierMetadata: Record<string, unknown>;
  canWithdraw: boolean;
  createdAt: string;
  updatedAt: string;
}

/** POST /api/v1/portal/submissions: a new open upload (one per file, or one per batch when the rule groups files). */
export const PortalSubmissionCreate = z.object({
  metadata: z.record(z.string(), z.unknown()).default({}),
  rightsVersion: z.number().int().min(1),
  rightsAccepted: z.literal(true),
});
export type PortalSubmissionCreate = z.infer<typeof PortalSubmissionCreate>;
/** POST /api/v1/portal/deliveries/:id/submit. */
export const PortalDeliverySubmit = z.object({
  metadata: z.record(z.string(), z.unknown()).default({}),
  rightsVersion: z.number().int().min(1),
  rightsAccepted: z.literal(true),
});
export type PortalDeliverySubmit = z.infer<typeof PortalDeliverySubmit>;
/** POST /api/v1/portal/uploads: the resumable upload of one file, for a submission's media or one requested item. */
export const PortalUploadCreate = z.object({
  filename: z.string().min(1).max(255),
  sizeBytes: z.number().int().positive(),
  submissionId: z.string().uuid().optional(),
  /** A delivery's requested item (the media item or an extra one). */
  itemId: z.string().uuid().optional(),
}).refine((b) => Boolean(b.submissionId) || Boolean(b.itemId), { message: 'An upload is for a submission or a requested item' });
export type PortalUploadCreate = z.infer<typeof PortalUploadCreate>;
export const PortalWithdrawBody = z.object({ reason: z.string().max(2000).default('') });

/* ================================================================ emails */

/** Supplier emails (the portal catalogue, overridable per workspace in 1.3.1: portal_email_texts). */
export const PORTAL_EMAIL_KINDS = [
  'portal_invitation', // P1
  'portal_password_reset', // P1
  'portal_two_step_reset', // P1
  'portal_assignment_new', // P2
  'portal_assignment_changed', // P2
  'portal_assignment_cancelled', // P2
  'portal_item_rejected', // P4
  'portal_submission_accepted', // P4
  'portal_submission_rejected', // P4
  'portal_staff_comment', // P4
  'portal_submission_received', // P3
  'portal_qc_result', // P3
  'portal_reminder', // 1.3.1
] as const;
export type PortalEmailKind = (typeof PORTAL_EMAIL_KINDS)[number];
/** Reviewer emails (topic "intake" in a member's email preferences). */
export const INTAKE_EMAIL_KINDS = ['intake_waiting', 'intake_supplier_reply', 'intake_overdue_digest', 'intake_withdrawn'] as const;
export type IntakeEmailKind = (typeof INTAKE_EMAIL_KINDS)[number];
/** The {placeholders} each supplier email may use (1.3.1 overrides refuse any other). */
export const PORTAL_EMAIL_PLACEHOLDERS: Record<PortalEmailKind, readonly string[]> = {
  portal_invitation: ['name', 'company', 'portalName', 'link', 'expires'],
  portal_password_reset: ['name', 'portalName', 'link'],
  portal_two_step_reset: ['name', 'portalName', 'link'],
  portal_assignment_new: ['name', 'company', 'portalName', 'title', 'assetCode', 'due', 'items', 'contact', 'link'],
  portal_assignment_changed: ['name', 'portalName', 'title', 'assetCode', 'due', 'items', 'contact', 'link'],
  portal_assignment_cancelled: ['name', 'portalName', 'title', 'assetCode'],
  portal_item_rejected: ['name', 'portalName', 'title', 'item', 'reason', 'link'],
  portal_submission_accepted: ['name', 'portalName', 'title', 'link'],
  portal_submission_rejected: ['name', 'portalName', 'title', 'reason', 'link'],
  portal_staff_comment: ['name', 'portalName', 'title', 'comment', 'link'],
  portal_submission_received: ['name', 'portalName', 'title', 'link'],
  portal_qc_result: ['name', 'portalName', 'title', 'result', 'errors', 'warnings', 'link'],
  portal_reminder: ['name', 'portalName', 'title', 'assetCode', 'due', 'link'],
};
export interface PortalEmailTextDTO { kind: PortalEmailKind; subject: string; body: string; overridden: boolean; placeholders: readonly string[] }

/* ================================================================ workflow triggers and audit */

/** Workflow triggers P4 adds (fired at acceptance, with the company, rule or spec and items in the context). */
export const PORTAL_TRIGGER_TYPES = ['delivery_accepted', 'portal_upload_accepted'] as const;
export type PortalTriggerType = (typeof PORTAL_TRIGGER_TYPES)[number];
/** The audit actions' prefixes: portal.* (suppliers, actor_kind 'portal', and staff configuration), intake.* (reviewers). */
export const PORTAL_AUDIT_PREFIXES = ['portal.', 'intake.', 'asset.intake_accept'] as const;

/* ================================================================ strings */

/**
 * The English catalogue of every portal string and email text. The portal app (apps/web/src/portal) and the emails
 * (services/api/src/modules/portal/emails-*.ts) read it through `t()`, so Finnish and Swedish can follow as more
 * catalogues of the same keys. Each stream has a file of its own in portal-strings/ (common is step 0's), keys under
 * its own prefix (identity., admin., assign., portal., intake.; email.<kind>.subject and .body for the kinds it
 * sends), and never edits another stream's.
 */
export const PORTAL_STRINGS = {
  ...COMMON_STRINGS,
  ...IDENTITY_STRINGS,
  ...ADMIN_STRINGS,
  ...ASSIGN_STRINGS,
  ...PORTAL_APP_STRINGS,
  ...INTAKE_STRINGS,
  // 1.3.1's streams (docs/dev/portal-1.3.1-build.md).
  ...EXTRAS_Q1_STRINGS,
  ...EXTRAS_Q2_STRINGS,
  ...EXTRAS_Q3_STRINGS,
  ...EXTRAS_Q4_STRINGS,
  // 1.4.0's streams (docs/dev/groups-1.4.0-build.md).
  ...GROUPS_G2_STRINGS,
  ...GROUPS_G3_STRINGS,
} as const satisfies Record<string, string>;
export type PortalStringKey = keyof typeof PORTAL_STRINGS;
export const PORTAL_LOCALES = ['en'] as const;
export type PortalLocale = (typeof PORTAL_LOCALES)[number];
const CATALOGUES: Record<PortalLocale, Partial<Record<string, string>>> = { en: PORTAL_STRINGS };

/**
 * A string from the catalogue with its {placeholders} filled in. An unknown key answers the key itself (so a missing
 * string shows, rather than nothing); a placeholder without a value stays as written.
 */
export function t(key: PortalStringKey | (string & {}), vars: Record<string, string | number> = {}, locale: PortalLocale = 'en'): string {
  const s = CATALOGUES[locale]?.[key] ?? CATALOGUES.en[key] ?? key;
  return s.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}
